import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Close as CancelIcon } from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import { useUserRoles } from '../../hooks/useUserRoles';

interface UserDetail {
  uuid: string;
  name: string;
  email: string;
  type: string;
  api_access_enabled: boolean;
  created_at: string;
  roles: string[];
}

interface UserDetailsTabProps {
  user: UserDetail;
  onUserUpdated: () => void;
}

const ALL_ROLES = [
  { value: 'USER', label: 'User' },
  { value: 'ORG_ADMIN', label: 'Organisation Admin' },
  { value: 'SUPER_ADMIN', label: 'Super Admin' }
];

export default function UserDetailsTab({ user, onUserUpdated }: UserDetailsTabProps) {
  const { isSuperAdmin } = useUserRoles();
  const availableRoles = isSuperAdmin()
    ? ALL_ROLES
    : ALL_ROLES.filter(r => r.value !== 'SUPER_ADMIN');

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: user.name,
    api_access_enabled: user.api_access_enabled,
    role: user.roles[0] || 'USER'
  });

  const handleEdit = () => {
    setFormData({
      name: user.name,
      api_access_enabled: user.api_access_enabled,
      role: user.roles[0] || 'USER'
    });
    setError(null);
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await apiClient.put(`/admin/users/${user.uuid}`, {
        name: formData.name,
        api_access_enabled: formData.api_access_enabled
      });

      if (formData.role !== (user.roles[0] || 'USER')) {
        await apiClient.put(`/admin/users/${user.uuid}/role`, {
          role: formData.role
        });
      }

      setEditing(false);
      onUserUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleTextChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSelectChange = (field: string) => (e: SelectChangeEvent<string>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSwitchChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.checked }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>User Details</Typography>
        {editing ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCancel} disabled={loading} startIcon={<CancelIcon />} size="small">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
              size="small"
            >
              Save Changes
            </Button>
          </Box>
        ) : (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit} size="small">
            Edit
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {editing ? (
            <>
              <TextField
                label="Full Name"
                value={formData.name}
                onChange={handleTextChange('name')}
                required
                disabled={loading}
                fullWidth
              />

              <TextField
                label="Email"
                value={user.email}
                disabled
                fullWidth
                helperText="Email cannot be changed"
              />

              <FormControl fullWidth required disabled={loading}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={handleSelectChange('role')}
                  label="Role"
                >
                  {availableRoles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {isSuperAdmin() && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.api_access_enabled}
                      onChange={handleSwitchChange('api_access_enabled')}
                      disabled={loading}
                    />
                  }
                  label="API Access Enabled"
                />
              )}
            </>
          ) : (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary">Full Name</Typography>
                <Typography variant="body1">{user.name}</Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{user.email}</Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">Role</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {user.roles.map((role) => (
                    <Chip
                      key={role}
                      label={role}
                      color={role === 'SUPER_ADMIN' ? 'error' : role === 'ORG_ADMIN' ? 'warning' : 'primary'}
                      variant="outlined"
                      size="small"
                      sx={{ mr: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">User Type</Typography>
                <Typography variant="body1">{user.type}</Typography>
              </Box>

              {isSuperAdmin() && (
                <Box>
                  <Typography variant="body2" color="text.secondary">API Access</Typography>
                  <Chip
                    label={user.api_access_enabled ? 'Enabled' : 'Disabled'}
                    color={user.api_access_enabled ? 'success' : 'default'}
                    variant="outlined"
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              )}

              <Box>
                <Typography variant="body2" color="text.secondary">Created</Typography>
                <Typography variant="body1">{formatDate(user.created_at)}</Typography>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
